import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { MapContainer, TileLayer, Marker, Circle } from 'react-leaflet';
import L from 'leaflet';
import { 
  Phone, Mail, MapPin, Clock, ChevronLeft,
  Users, Briefcase, Shield, MessageCircle, Building2
} from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import HireHubLogo, { HireHubIcon } from '../components/HireHubLogo';

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom brand marker
const createBrandMarker = () => {
  return L.divIcon({
    className: 'custom-brand-marker',
    html: `
      <div style="
        width: 40px;
        height: 40px;
        background: #004B8D;
        border: 3px solid white;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        box-shadow: 0 3px 10px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <div style="
          width: 10px;
          height: 10px;
          background: white;
          border-radius: 50%;
          transform: rotate(45deg);
        "></div>
      </div>
    `,
    iconSize: [40, 40],
    iconAnchor: [20, 40],
  });
};

// Contact information
const CONTACT_INFO = {
  phone: '+61 406 857 892',
  phoneHref: 'tel:+61406857892',
  location: 'Pimpama, QLD, Australia 4209',
  coordinates: { lat: -27.8227, lng: 153.3052 }, // Pimpama coordinates
  emails: {
    support: 'support@hirehubaustralia.com',
    legal: 'legal@hirehubaustralia.com',
    general: 'noreply@hirehubaustralia.com'
  },
  businessHours: 'Monday - Friday, 9am - 5pm AEST'
};

// Contact category card
const ContactCard = ({ icon: Icon, title, description, action, actionHref, actionLabel, color }) => (
  <Card className="border-slate-100 hover:border-slate-200 transition-colors h-full">
    <CardContent className="p-5 sm:p-6 flex flex-col h-full">
      <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center mb-4`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <h3 className="font-semibold text-lg text-slate-900 mb-2">{title}</h3>
      <p className="text-slate-600 text-sm mb-4 flex-grow">{description}</p>
      {actionHref && (
        <a 
          href={actionHref}
          className="inline-flex items-center gap-2 text-[#004B8D] font-medium text-sm hover:underline"
        >
          {actionLabel}
          <ChevronLeft className="w-4 h-4 rotate-180" />
        </a>
      )}
    </CardContent>
  </Card>
);

const ContactPage = () => {
  const [mapReady, setMapReady] = useState(false);
  const brandMarker = React.useMemo(() => createBrandMarker(), []);

  useEffect(() => {
    // Small delay to ensure map renders correctly
    const timer = setTimeout(() => setMapReady(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 sm:gap-4">
            <Link to="/">
              <Button variant="ghost" size="icon" className="h-9 w-9 sm:h-10 sm:w-10">
                <ChevronLeft className="w-5 h-5" />
              </Button>
            </Link>
            <Link to="/">
              <HireHubLogo size="md" />
            </Link>
          </div>
          <Link to="/vehicles">
            <Button variant="outline" size="sm" className="h-9 text-sm">
              Browse Vehicles
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 py-12 sm:py-16 text-center">
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            Contact Us
          </h1>
          <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto">
            Need help with a booking, account, or host listing? Get in touch with HireHub Australia. 
            We're here to support both renters and hosts.
          </p>
        </div>
      </section>

      {/* Contact Categories */}
      <section className="py-10 sm:py-14">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            <ContactCard 
              icon={Users}
              title="Renter Support"
              description="Questions about bookings, payments, or vehicle pickups? Our team is ready to help renters."
              actionHref={`mailto:${CONTACT_INFO.emails.support}?subject=Renter%20Support%20Enquiry`}
              actionLabel="Email Support"
              color="bg-blue-500"
            />
            <ContactCard 
              icon={Briefcase}
              title="Host Support"
              description="Need help with your listing, payouts, or managing bookings? We support our hosts."
              actionHref={`mailto:${CONTACT_INFO.emails.support}?subject=Host%20Support%20Enquiry`}
              actionLabel="Email Support"
              color="bg-purple-500"
            />
            <ContactCard 
              icon={Shield}
              title="Claims & Disputes"
              description="Have a damage claim or dispute? Our admin team reviews all cases with photo evidence."
              actionHref={`mailto:${CONTACT_INFO.emails.support}?subject=Claim%20or%20Dispute%20Enquiry`}
              actionLabel="Email Claims Team"
              color="bg-amber-500"
            />
            <ContactCard 
              icon={MessageCircle}
              title="General Enquiries"
              description="For partnerships, media, or general questions about HireHub Australia."
              actionHref={`mailto:${CONTACT_INFO.emails.support}?subject=General%20Enquiry`}
              actionLabel="Get in Touch"
              color="bg-cyan-500"
            />
          </div>
        </div>
      </section>

      {/* Contact Details & Map */}
      <section className="py-10 sm:py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Contact Details */}
            <div>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mb-6">
                Get in Touch
              </h2>
              
              <div className="space-y-6">
                {/* Phone */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Phone</h3>
                    <a 
                      href={CONTACT_INFO.phoneHref}
                      className="text-[#004B8D] font-medium hover:underline text-lg"
                      data-testid="contact-phone"
                    >
                      {CONTACT_INFO.phone}
                    </a>
                    <p className="text-sm text-slate-500 mt-1">Call us during business hours</p>
                  </div>
                </div>

                {/* Email - Support */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Support Email</h3>
                    <a 
                      href={`mailto:${CONTACT_INFO.emails.support}`}
                      className="text-[#004B8D] font-medium hover:underline"
                      data-testid="contact-email-support"
                    >
                      {CONTACT_INFO.emails.support}
                    </a>
                    <p className="text-sm text-slate-500 mt-1">For booking, account, and listing help</p>
                  </div>
                </div>

                {/* Email - Legal */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Legal & Privacy</h3>
                    <a 
                      href={`mailto:${CONTACT_INFO.emails.legal}`}
                      className="text-[#004B8D] font-medium hover:underline"
                      data-testid="contact-email-legal"
                    >
                      {CONTACT_INFO.emails.legal}
                    </a>
                    <p className="text-sm text-slate-500 mt-1">For legal, privacy, and compliance requests</p>
                  </div>
                </div>

                {/* Location */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Location</h3>
                    <p className="text-slate-700" data-testid="contact-location">
                      {CONTACT_INFO.location}
                    </p>
                    <p className="text-sm text-slate-500 mt-1">HireHub Australia service area</p>
                  </div>
                </div>

                {/* Business Hours */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Business Hours</h3>
                    <p className="text-slate-700">{CONTACT_INFO.businessHours}</p>
                    <p className="text-sm text-slate-500 mt-1">We aim to respond as soon as possible</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Map */}
            <div>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mb-6">
                Our Location
              </h2>
              
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <div className="aspect-[4/3] relative">
                  {mapReady && (
                    <MapContainer
                      center={[CONTACT_INFO.coordinates.lat, CONTACT_INFO.coordinates.lng]}
                      zoom={13}
                      style={{ height: '100%', width: '100%' }}
                      zoomControl={false}
                      scrollWheelZoom={false}
                      dragging={false}
                      doubleClickZoom={false}
                    >
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      <Circle
                        center={[CONTACT_INFO.coordinates.lat, CONTACT_INFO.coordinates.lng]}
                        radius={2000}
                        pathOptions={{
                          color: '#004B8D',
                          fillColor: '#004B8D',
                          fillOpacity: 0.1,
                          weight: 2,
                          opacity: 0.5
                        }}
                      />
                      <Marker 
                        position={[CONTACT_INFO.coordinates.lat, CONTACT_INFO.coordinates.lng]}
                        icon={brandMarker}
                      />
                    </MapContainer>
                  )}
                </div>
                
                {/* Location info below map */}
                <div className="p-4 bg-slate-50 border-t border-slate-100">
                  <div className="flex items-center gap-3">
                    <Building2 className="w-5 h-5 text-[#004B8D]" />
                    <div>
                      <p className="font-medium text-slate-900">HireHub Australia</p>
                      <p className="text-sm text-slate-500">Service area: South East Queensland</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Response Expectation */}
      <section className="py-10 sm:py-14">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="bg-blue-50 rounded-2xl p-6 sm:p-8">
            <Clock className="w-10 h-10 text-[#004B8D] mx-auto mb-4" />
            <h3 className="font-heading text-xl sm:text-2xl font-bold text-slate-900 mb-3">
              Response Times
            </h3>
            <p className="text-slate-600">
              We aim to respond to all enquiries as soon as possible during business hours. 
              For urgent booking issues, please call us directly. Support tickets are typically 
              answered within 24-48 hours.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 sm:py-10 px-4 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <HireHubIcon size={32} className="rounded-lg" />
              <span className="font-heading font-bold">HireHub Australia</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-slate-400">
              <Link to="/legal/terms" className="hover:text-white transition-colors">Terms</Link>
              <Link to="/legal/privacy" className="hover:text-white transition-colors">Privacy</Link>
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-slate-800 text-center text-slate-500 text-xs sm:text-sm">
            <p>© {new Date().getFullYear()} HireHub Australia. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;
